// src/features/strategies/new.tsx

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { Clients } from "@/lib/clients";
import { ClientTables } from "@/lib/clientTables";

import {
  AutocompleteAsyncField,
  CheckboxField,
  DatePickerField,
  InputField,
  SelectField,
  TextareaField,
} from "@/features/FormCard/Fields";

import { FormActionsNew } from "@/features/FormCard/Actions";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";

import {
  dateSchema,
  generateDefaultValues,
  parseToDateValue,
  parseYes,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";

import {
  adjustDataForSubmission,
  preprocessCountryData,
} from "@/components/Form/CountryPhone/data";

import {
  assetClassOptions,
  bondTypeOptions,
  derivativeTypeOptions,
  frequencyOptions,
  interestRateTypeOptions,
  instrumentOptions,
  returnTypeOptions,
  statusOptions,
  typeOptions,
} from "./filter";
import fieldTranslations from "./translations";

const client = Clients.QTL;
const table_name = ClientTables.QTL_STRATEGIES;

// Zod schema: all optional except `name`
const FormSchema = z.object({
  // General
  short_name: z.string().nullable().optional(),
  name: z.string().min(1, "Name is required"),
  demo_alias_name: z.string().nullable().optional(),

  // Currency autocomplete field (display field)
  currency_text: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string().optional().nullable(),
        z.object({
          id: z.number(),
          en: z.string(),
          text: z.string(),
        }),
      ])
      .optional(),
  ),
  // Currency ID field
  currency: z.coerce.number().optional().nullable(),

  // Foreign keys (display+ID)
  company: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string().optional().nullable(),
        z.object({ id: z.number(), name: z.string() }),
      ])
      .optional(),
  ),
  company_id: z.coerce.number().optional().nullable(),

  issuer: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string().optional().nullable(),
        z.object({ id: z.number(), name: z.string() }),
      ])
      .optional(),
  ),
  issuer_id: z.coerce.number().optional().nullable(),

  risk_free_benchmark: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string().optional().nullable(),
        z.object({ id: z.number(), name: z.string() }),
      ])
      .optional(),
  ),
  risk_free_benchmark_id: z.coerce.number().optional().nullable(),

  benchmark: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string().optional().nullable(),
        z.object({ id: z.number(), name: z.string() }),
      ])
      .optional(),
  ),
  benchmark_id: z.coerce.number().optional().nullable(),

  // Codes
  isin: z.string().nullable().optional(),
  ticker: z.string().nullable().optional(),
  reuters_ticker: z.string().nullable().optional(),
  yahoo_ticker: z.string().nullable().optional(),
  code: z.string().nullable().optional(),
  provider_compo_code: z.string().nullable().optional(),
  provider_fixings_code: z.string().nullable().optional(),

  // Internal Fields
  status: z.coerce.number().optional().nullable(),
  type: z.string().nullable().optional(),
  priority: z.coerce.number().optional().nullable(),

  user: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string().optional().nullable(),
        z.object({ id: z.number(), email: z.string() }),
      ])
      .optional(),
  ),
  user_id: z.coerce.number().optional().nullable(),

  calculation_agent: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string().optional().nullable(),
        z.object({ id: z.number(), name: z.string() }),
      ])
      .optional(),
  ),
  calculation_agent_id: z.coerce.number().optional().nullable(),

  administrator: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string().optional().nullable(),
        z.object({ id: z.number(), name: z.string() }),
      ])
      .optional(),
  ),
  administrator_id: z.coerce.number().optional().nullable(),

  provider_url: z.string().optional().nullable(),
  source_url: z.string().nullable().optional(),
  open_figi_id: z.coerce.number().optional().nullable(),

  is_downloadable: z.preprocess(
    (v) => parseYes(v as string),
    z.boolean().optional(),
  ),
  is_benchmark: z.preprocess(
    (v) => parseYes(v as string),
    z.boolean().optional(),
  ),
  is_allowed_to_us: z.preprocess(
    (v) => parseYes(v as string),
    z.boolean().optional(),
  ),
  is_green_bond: z.boolean().default(false), // define as DEFAULT false in db
  is_long_short: z.preprocess(
    (v) => parseYes(v as string),
    z.boolean().optional(),
  ),
  allow_past_fixings_data: z.preprocess(
    (v) => parseYes(v as string),
    z.boolean().optional(),
  ),

  // Fund fields
  is_main_share_class: z.preprocess(
    (v) => parseYes(v as string),
    z.boolean().optional(),
  ),
  share_class: z.string().nullable().optional(),
  fund_name: z.string().nullable().optional(),
  fund_currency: z.string().nullable().optional(),
  redemption_rate: z.coerce.number().optional().nullable(),
  etf_leverage_factor: z.coerce.number().optional().nullable(),
  financial_period_end: z
    .preprocess((v) => parseToDateValue(v as string), dateSchema)
    .optional(),

  // Derivative fields
  derivative_underlying_name: z.string().nullable().optional(),
  derivative_underlying_code: z.string().nullable().optional(),

  // Static Data
  live_date: z
    .preprocess((v) => parseToDateValue(v as string), dateSchema)
    .optional(),
  description: z.string().nullable().optional(),

  // Country of registration autocomplete field (display field)
  country_of_registration: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string().optional().nullable(),
        z.object({
          id: z.string(),
          text: z.string(),
          code: z.string(),
          fr_translation: z.string(),
        }),
      ])
      .optional(),
  ),
  // Country ID field (ISO code) - can be string or number
  country_of_registration_code: z
    .union([z.string(), z.coerce.string()])
    .optional()
    .nullable(),
  publication_delay: z.coerce.number().optional().nullable(),

  frequency: z.coerce.number().optional().nullable(),
  instrument: z.coerce.number().optional().nullable(),
  alpha_beta: z.coerce.number().optional().nullable(),
  asset_class: z.coerce.number().optional().nullable(),
  risk_factor: z.coerce.number().optional().nullable(),
  bond_type: z.coerce.number().optional().nullable(),
  interest_rate_type: z.string().nullable().optional(),
  counterparty_issuer_id: z.coerce.number().optional().nullable(),

  derivative_type: z.coerce.number().optional().nullable(),
  derivative_strike: z.coerce.number().optional().nullable(),
  tranche: z.coerce.number().optional().nullable(),
  coupon: z.coerce.number().optional().nullable(),
  maturity: z
    .preprocess((v) => parseToDateValue(v as string), dateSchema)
    .optional(),

  aum_fund_level_currency: z.string().nullable().optional(),
  aum_share_class_currency: z.string().nullable().optional(),
  is_hedged: z.preprocess((v) => parseYes(v as string), z.boolean().optional()),
  is_institutional_share_class: z.preprocess(
    (v) => parseYes(v as string),
    z.boolean().optional(),
  ),
  contract_size: z.coerce.number().optional().nullable(),
  return_type: z.coerce.number().optional().nullable(),

  // Ratings
  rating: z.coerce.number().optional().nullable(),
  barcap_rating: z.string().nullable().optional(),
  citi_rating: z.string().nullable().optional(),
  moodys_rating: z.string().nullable().optional(),
  sp_rating: z.string().nullable().optional(),
  bloomberg_rating: z.string().nullable().optional(),

  // Composition Static Data
  compo_publication_delay: z.coerce.number().optional().nullable(),
  compo_frequency: z.coerce.number().optional().nullable(),

  replicated_strategy: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string().optional().nullable(),
        z.object({ id: z.number(), name: z.string() }),
      ])
      .optional(),
  ),
  replicated_strategy_id: z.coerce.number().optional().nullable(),

  // Derivative Underlying AutocompleteAsyncField (display field)
  derivative_underlying: z.preprocess(
    (e: any) => preprocessTextAsyncAutocompleteField(e),
    z
      .union([
        z.string().optional().nullable(),
        z.object({ id: z.number(), name: z.string() }),
      ])
      .optional(),
  ),
  // Derivative Underlying ID field
  derivative_underlying_id: z.preprocess((v) => {
    if (
      v === null ||
      v === undefined ||
      v === "" ||
      (typeof v === "number" && isNaN(v))
    ) {
      return null;
    }
    return v;
  }, z.coerce.number().optional().nullable()),
});

export function StrategiesNew() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
    disable_translations_request: true,
    adjustData: adjustDataForSubmission,
    adjustOnReset: preprocessCountryData,
  });
  const DEFAULTS = generateDefaultValues(FormSchema, {});

  const onResetNew = () => {
    form.reset(DEFAULTS, {
      keepDirty: false,
      keepTouched: false,
      keepErrors: false,
      keepValues: false,
    });
    handleReset();
  };

  const navigateToList = () => navigate(-1);

  return (
    <div className="grid place-items-center h-full w-full">
      <div className="w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("actions.new")}{" "}
                {t("strategy.title_singular", {
                  ns: "tables",
                  defaultValue: "Strategy",
                })}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                {/* General */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="name"
                    label={t(`strategy.fields.name`, {
                      ns: "tables",
                      defaultValue: "Name",
                    })}
                    isRequired
                    control={form.control}
                    capitalize={true}
                  />
                  <InputField
                    name="short_name"
                    label={t(`strategy.fields.short_name`, {
                      ns: "tables",
                      defaultValue: "Short Name",
                    })}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="demo_alias_name"
                    label={t(`strategy.fields.demo_alias_name`, {
                      ns: "tables",
                      defaultValue: "Demo Alias",
                    })}
                    control={form.control}
                  />
                  <AutocompleteAsyncField
                    name="currency_text"
                    label={t(`strategy.fields.currency_text`, {
                      ns: "tables",
                      defaultValue: "Currency",
                    })}
                    base_url="instit-quantilia/countries"
                    column_name="currency"
                    id_column="currency"
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="company"
                    label={t(`strategy.fields.company`, {
                      ns: "tables",
                      defaultValue: "Company",
                    })}
                    base_url="instit-quantilia/companies"
                    column_name="name"
                    id_column="company_id"
                    control={form.control}
                  />
                  <AutocompleteAsyncField
                    name="issuer"
                    label={t(`strategy.fields.issuer`, {
                      ns: "tables",
                      defaultValue: "Issuer",
                    })}
                    base_url="instit-quantilia/issuers"
                    column_name="name"
                    id_column="issuer_id"
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="risk_free_benchmark"
                    label={t(`strategy.fields.risk_free_benchmark`, {
                      ns: "tables",
                      defaultValue: "Risk Free Benchmark",
                    })}
                    base_url={`${Clients.QTL}/${ClientTables.QTL_STRATEGIES}`}
                    column_name="name"
                    id_column="risk_free_benchmark_id"
                    control={form.control}
                  />
                  <AutocompleteAsyncField
                    name="benchmark"
                    label={t(`strategy.fields.benchmark`, {
                      ns: "tables",
                      defaultValue: "Benchmark",
                    })}
                    base_url={`${Clients.QTL}/${ClientTables.QTL_STRATEGIES}`}
                    column_name="name"
                    id_column="benchmark_id"
                    control={form.control}
                  />
                </div>

                {/* Codes */}
                <Divider />
                <CardHeader className="text-md font-semibold text-gray-700">
                  {t("strategy.sections.codes", {
                    ns: "tables",
                    defaultValue: "Codes",
                  })}
                </CardHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="isin"
                    label={t(`${table_name}.columns.isin`, {
                      ns: "tables",
                      defaultValue: "ISIN",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="ticker"
                    label={t(`strategy.fields.ticker`, {
                      ns: "tables",
                      defaultValue: "Ticker",
                    })}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="reuters_ticker"
                    label={t(`strategy.fields.reuters_ticker`, {
                      ns: "tables",
                      defaultValue: "Reuters Ticker",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="yahoo_ticker"
                    label={t(`strategy.fields.yahoo_ticker`, {
                      ns: "tables",
                      defaultValue: "Yahoo Ticker",
                    })}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="code"
                    label={t(`strategy.fields.code`, {
                      ns: "tables",
                      defaultValue: "Code",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="provider_compo_code"
                    label={t(`strategy.fields.provider_compo_code`, {
                      ns: "tables",
                      defaultValue: "Provider Compo Code",
                    })}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="provider_fixings_code"
                    label={t(`strategy.fields.provider_fixings_code`, {
                      ns: "tables",
                      defaultValue: "Provider Fixings Code",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Internal Fields */}
                <Divider />
                <CardHeader className="text-md font-semibold text-gray-700">
                  {t("strategy.sections.internal_fields", {
                    ns: "tables",
                    defaultValue: "Internal Fields",
                  })}
                </CardHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="status"
                    label={t(`strategy.fields.status`, {
                      ns: "tables",
                      defaultValue: "Status",
                    })}
                    options={statusOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <SelectField
                    name="type"
                    label={t(`strategy.fields.type`, {
                      ns: "tables",
                      defaultValue: "Type",
                    })}
                    options={typeOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="priority"
                    label={t(`strategy.fields.priority`, {
                      ns: "tables",
                      defaultValue: "Priority",
                    })}
                    type="number"
                    control={form.control}
                  />
                  <AutocompleteAsyncField
                    name="user"
                    label={t(`strategy.fields.user`, {
                      ns: "tables",
                      defaultValue: "User",
                    })}
                    base_url={`${Clients.QTL}/${ClientTables.QTL_STRATEGIES}`}
                    column_name="user_email"
                    id_column="user_id"
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="calculation_agent"
                    label={t(`strategy.fields.calculation_agent`, {
                      ns: "tables",
                      defaultValue: "Calculation Agent",
                    })}
                    base_url="instit-quantilia/companies"
                    column_name="name"
                    id_column="calculation_agent_id"
                    control={form.control}
                  />
                  <AutocompleteAsyncField
                    name="administrator"
                    label={t(`strategy.fields.administrator`, {
                      ns: "tables",
                      defaultValue: "Administrator",
                    })}
                    base_url="instit-quantilia/companies"
                    column_name="name"
                    id_column="administrator_id"
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="provider_url"
                    label={t(`strategy.fields.provider_url`, {
                      ns: "tables",
                      defaultValue: "Provider URL",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="source_url"
                    label={t(`strategy.fields.source_url`, {
                      ns: "tables",
                      defaultValue: "Source URL",
                    })}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="open_figi_id"
                    label={t(`strategy.fields.open_figi_id`, {
                      ns: "tables",
                      defaultValue: "Open FIGI ID",
                    })}
                    type="number"
                    control={form.control}
                  />
                  <div className="flex flex-col space-y-2">
                    <CheckboxField
                      name="is_downloadable"
                      label={t(`strategy.fields.is_downloadable`, {
                        ns: "tables",
                        defaultValue: "Is Downloadable",
                      })}
                      control={form.control}
                    />
                    <CheckboxField
                      name="is_benchmark"
                      label={t(`strategy.fields.is_benchmark`, {
                        ns: "tables",
                        defaultValue: "Is Benchmark",
                      })}
                      control={form.control}
                    />
                  </div>
                  <div className="flex flex-col space-y-2">
                    <CheckboxField
                      name="is_allowed_to_us"
                      label={t(`strategy.fields.is_allowed_to_us`, {
                        ns: "tables",
                        defaultValue: "Is Allowed to US",
                      })}
                      control={form.control}
                    />
                    <CheckboxField
                      name="is_long_short"
                      label={t(`strategy.fields.is_long_short`, {
                        ns: "tables",
                        defaultValue: "Is Long Short",
                      })}
                      control={form.control}
                    />
                  </div>
                  <div className="flex flex-col space-y-2">
                    <CheckboxField
                      name="allow_past_fixings_data"
                      label={t(`strategy.fields.allow_past_fixings_data`, {
                        ns: "tables",
                        defaultValue: "Allow Past Fixings Data",
                      })}
                      control={form.control}
                    />
                  </div>
                </div>

                {/* Fund Fields */}
                <Divider />
                <CardHeader className="text-md font-semibold text-gray-700">
                  {t("strategy.sections.fund_fields", {
                    ns: "tables",
                    defaultValue: "Fund Fields",
                  })}
                </CardHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="fund_name"
                    label={t(`strategy.fields.fund_name`, {
                      ns: "tables",
                      defaultValue: "Fund Name",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="fund_currency"
                    label={t(`strategy.fields.fund_currency`, {
                      ns: "tables",
                      defaultValue: "Fund Currency",
                    })}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="redemption_rate"
                    label={t(`strategy.fields.redemption_rate`, {
                      ns: "tables",
                      defaultValue: "Redemption Rate",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="etf_leverage_factor"
                    label={t(`strategy.fields.etf_leverage_factor`, {
                      ns: "tables",
                      defaultValue: "ETF Leverage Factor",
                    })}
                    type="number"
                    control={form.control}
                  />
                  <DatePickerField
                    name="financial_period_end"
                    label={t(`strategy.fields.financial_period_end`, {
                      ns: "tables",
                      defaultValue: "Financial Period End",
                    })}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="share_class"
                    label={t(`strategy.fields.share_class`, {
                      ns: "tables",
                      defaultValue: "Share Class",
                    })}
                    control={form.control}
                  />
                  <CheckboxField
                    name="is_main_share_class"
                    label={t(`strategy.fields.is_main_share_class`, {
                      ns: "tables",
                      defaultValue: "Is Main Share Class",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Derivative Fields */}
                <Divider />
                <CardHeader className="text-md font-semibold text-gray-700">
                  {t("strategy.sections.derivative_fields", {
                    ns: "tables",
                    defaultValue: "Derivative Fields",
                  })}
                </CardHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="derivative_underlying_name"
                    label={t(`strategy.fields.derivative_underlying_name`, {
                      ns: "tables",
                      defaultValue: "Underlying Name",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="derivative_underlying_code"
                    label={t(`strategy.fields.derivative_underlying_code`, {
                      ns: "tables",
                      defaultValue: "Underlying Code",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Static Data */}
                <Divider />
                <CardHeader className="text-md font-semibold text-gray-700">
                  {t("strategy.sections.static_data", {
                    ns: "tables",
                    defaultValue: "Static Data",
                  })}
                </CardHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name="maturity"
                    label={t(`strategy.fields.maturity`, {
                      ns: "tables",
                      defaultValue: "Maturity",
                    })}
                    control={form.control}
                  />
                  <DatePickerField
                    name="live_date"
                    label={t(`strategy.fields.live_date`, {
                      ns: "tables",
                      defaultValue: "Live Date",
                    })}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="coupon"
                    label={t(`strategy.fields.coupon`, {
                      ns: "tables",
                      defaultValue: "Coupon",
                    })}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="tranche"
                    label={t(`strategy.fields.tranche`, {
                      ns: "tables",
                      defaultValue: "Tranche",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="publication_delay"
                    label={t(`strategy.fields.publication_delay`, {
                      ns: "tables",
                      defaultValue: "Publication Delay",
                    })}
                    type="number"
                    control={form.control}
                  />
                  <SelectField
                    name="frequency"
                    label={t(`strategy.fields.frequency`, {
                      ns: "tables",
                      defaultValue: "Frequency",
                    })}
                    options={frequencyOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="instrument"
                    label={t(`strategy.fields.instrument`, {
                      ns: "tables",
                      defaultValue: "Instrument",
                    })}
                    options={instrumentOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <InputField
                    name="alpha_beta"
                    label={t(`strategy.fields.alpha_beta`, {
                      ns: "tables",
                      defaultValue: "Alpha/Beta",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="asset_class"
                    label={t(`strategy.fields.asset_class`, {
                      ns: "tables",
                      defaultValue: "Asset Class",
                    })}
                    options={assetClassOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <InputField
                    name="risk_factor"
                    label={t(`strategy.fields.risk_factor`, {
                      ns: "tables",
                      defaultValue: "Risk Factor",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="bond_type"
                    label={t(`strategy.fields.bond_type`, {
                      ns: "tables",
                      defaultValue: "Bond Type",
                    })}
                    options={bondTypeOptions}
                    control={form.control}
                  />
                  <InputField
                    name="counterparty_issuer_id"
                    label={t(`strategy.fields.counterparty_issuer_id`, {
                      ns: "tables",
                      defaultValue: "Counterparty Issuer ID",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="interest_rate_type"
                    label={t(`strategy.fields.interest_rate_type`, {
                      ns: "tables",
                      defaultValue: "Interest Rate Type",
                    })}
                    options={interestRateTypeOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <CheckboxField
                    name="is_green_bond"
                    label={t(`strategy.fields.is_green_bond`, {
                      ns: "tables",
                    })}
                    control={form.control}
                    className="md:flex md:items-center md:h-full md:pt-6"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    name="derivative_type"
                    label={t(`strategy.fields.derivative_type`, {
                      ns: "tables",
                      defaultValue: "Derivative Type",
                    })}
                    options={derivativeTypeOptions}
                    control={form.control}
                    allow_empty_option
                  />
                  <InputField
                    name="derivative_strike"
                    label={t(`strategy.fields.derivative_strike`, {
                      ns: "tables",
                      defaultValue: "Derivative Strike",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="aum_fund_level_currency"
                    label={t(`strategy.fields.aum_fund_level_currency`, {
                      ns: "tables",
                      defaultValue: "AUM Fund Level Currency",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="aum_share_class_currency"
                    label={t(`strategy.fields.aum_share_class_currency`, {
                      ns: "tables",
                      defaultValue: "AUM Share Class Currency",
                    })}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="contract_size"
                    label={t(`strategy.fields.contract_size`, {
                      ns: "tables",
                      defaultValue: "Contract Size",
                    })}
                    type="number"
                    control={form.control}
                  />
                  <SelectField
                    name="return_type"
                    label={t(`strategy.fields.return_type`, {
                      ns: "tables",
                      defaultValue: "Return Type",
                    })}
                    options={returnTypeOptions}
                    control={form.control}
                    allow_empty_option
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <TextareaField
                    name="description"
                    label={t(`strategy.fields.description`, {
                      ns: "tables",
                      defaultValue: "Description",
                    })}
                    control={form.control}
                  />
                  <div className="flex flex-col space-y-2">
                    <AutocompleteAsyncField
                      name="country_of_registration"
                      label={t(`strategy.fields.country_of_registration`, {
                        ns: "tables",
                        defaultValue: "Country of Registration",
                      })}
                      base_url="instit-quantilia/countries"
                      column_name="en_translation"
                      id_column="country_of_registration_code"
                      control={form.control}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <CheckboxField
                        name="is_institutional_share_class"
                        label={t(
                          `strategy.fields.is_institutional_share_class`,
                          {
                            ns: "tables",
                            defaultValue: "Is Institutional Share Class",
                          },
                        )}
                        control={form.control}
                      />
                      <CheckboxField
                        name="is_hedged"
                        label={t(`strategy.fields.is_hedged`, {
                          ns: "tables",
                          defaultValue: "Is Hedged",
                        })}
                        control={form.control}
                      />
                    </div>
                  </div>
                </div>

                {/* Ratings */}
                <Divider />
                <CardHeader className="text-md font-semibold text-gray-700">
                  {t("strategy.sections.ratings", {
                    ns: "tables",
                    defaultValue: "Ratings",
                  })}
                </CardHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="rating"
                    label={t(`strategy.fields.rating`, {
                      ns: "tables",
                      defaultValue: "Rating",
                    })}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="barcap_rating"
                    label={t(`strategy.fields.barcap_rating`, {
                      ns: "tables",
                      defaultValue: "Barcap Rating",
                    })}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="citi_rating"
                    label={t(`strategy.fields.citi_rating`, {
                      ns: "tables",
                      defaultValue: "Citi Rating",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="moodys_rating"
                    label={t(`strategy.fields.moodys_rating`, {
                      ns: "tables",
                      defaultValue: "Moody's Rating",
                    })}
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="sp_rating"
                    label={t(`strategy.fields.sp_rating`, {
                      ns: "tables",
                      defaultValue: "S&P Rating",
                    })}
                    control={form.control}
                  />
                  <InputField
                    name="bloomberg_rating"
                    label={t(`strategy.fields.bloomberg_rating`, {
                      ns: "tables",
                      defaultValue: "Bloomberg Rating",
                    })}
                    control={form.control}
                  />
                </div>

                {/* Composition Static Data */}
                <Divider />
                <CardHeader className="text-md font-semibold text-gray-700">
                  {t("strategy.sections.composition_static_data", {
                    ns: "tables",
                    defaultValue: "Composition Static Data",
                  })}
                </CardHeader>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    name="compo_publication_delay"
                    label={t(`strategy.fields.compo_publication_delay`, {
                      ns: "tables",
                      defaultValue: "Compo Publication Delay",
                    })}
                    type="number"
                    control={form.control}
                  />
                  <InputField
                    name="compo_frequency"
                    label={t(`strategy.fields.compo_frequency`, {
                      ns: "tables",
                      defaultValue: "Compo Frequency",
                    })}
                    type="number"
                    control={form.control}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <AutocompleteAsyncField
                    name="replicated_strategy"
                    label={t(`strategy.fields.replicated_strategy`, {
                      ns: "tables",
                      defaultValue: "Replicated Strategy",
                    })}
                    base_url={`${Clients.QTL}/${ClientTables.QTL_STRATEGIES}`}
                    column_name="name"
                    id_column="replicated_strategy_id"
                    control={form.control}
                  />
                  <AutocompleteAsyncField
                    name="derivative_underlying"
                    label={t(`strategy.fields.derivative_underlying`, {
                      ns: "tables",
                      defaultValue: "Derivative Underlying",
                    })}
                    base_url={`${Clients.QTL}/${ClientTables.QTL_STRATEGIES}`}
                    column_name="name"
                    id_column="derivative_underlying_id"
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={onResetNew}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>

          {/* Confirmation */}
          <ConfirmationModal
            isOpen={isConfirmationModalOpen}
            isLoading={isUpdating}
            onClose={cancelUpdate}
            onConfirm={confirmUpdate}
            title={t("confirm_update")}
          >
            {changedFields
              ? NewDiff(
                  changedFields,
                  [
                    "company_id",
                    "issuer_id",
                    "risk_free_benchmark_id",
                    "benchmark_id",
                    "user_id",
                    "calculation_agent_id",
                    "administrator_id",
                    "replicated_strategy_id",
                    "derivative_underlying_id",
                  ],
                  fieldTranslations,
                  "strategy",
                )
              : t("no_changes")}
          </ConfirmationModal>

          {/* Cancel */}
          <CancelConfirmationModal
            isOpen={isDeleteModalOpen}
            onClose={closeDeleteModal}
            onCancel={navigateToList}
          />
        </Card>
      </div>
    </div>
  );
}
